import './scss/rivandy.scss';
import './scss/statistics.scss';
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { resolveCharacter, resolveReincarnation } from './historyData';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, ArcElement, Tooltip, Legend);

const numberFormat = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatSeconds = (seconds) => {
  const total = Number(seconds) || 0;
  if (total <= 0) return '0 min';
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const parts = [];
  if (hours) parts.push(`${hours} h`);
  if (minutes || parts.length === 0) parts.push(`${minutes} min`);
  return parts.join(' ');
};

const shortDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
};

const palette = ['#7dd3a9', '#f9b233', '#7bd5f5', '#f9a8d4', '#c084fc', '#9bd4b8', '#f47373', '#7cc7ff', '#fbc38d'];

function Statistics() {
  const [stats, setStats] = useState(null);
  const [activeMode, setActiveMode] = useState('all');
  const [sortMetric, setSortMetric] = useState('matches');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [trendInterval, setTrendInterval] = useState('daily');
  const [trendRange, setTrendRange] = useState(180);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get('https://rivandy.com:3003/game/GetStats');
        setStats(data || {});
        setError(null);
      } catch (err) {
        setError('Unable to load statistics right now.');
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const modeStats = useMemo(() => {
    const sections = [
      { id: 'all', label: 'All modes', data: stats },
      { id: 'Survivor', label: 'Survivor', data: stats?.modes?.Survivor },
      { id: 'BattleRoyale', label: 'Battle Royale', data: stats?.modes?.BattleRoyale },
    ].filter((s) => s.data);
    if (!sections.find((s) => s.id === activeMode) && sections.length > 0) {
      setActiveMode(sections[0].id);
    }
    return sections;
  }, [stats, activeMode]);

  const current = modeStats.find((s) => s.id === activeMode)?.data || {};

  const totals = current?.totals || {};
  const gamesByMode = current?.gamesByMode || [];
  const gamesPerDay = current?.gamesPerDay || [];
  const gamesPerWeek = current?.gamesPerWeek || [];
  const gamesPerMonth = current?.gamesPerMonth || [];
  const gamesPerYear = current?.gamesPerYear || [];
  const characters = current?.characters || [];
  const spiritUsage = current?.spiritUsage || [];
  const winnerByCharacter = current?.winnerByCharacter || [];
  const winnerBySpirit = current?.winnerBySpirit || [];
  const spiritPlaysByPlayer = current?.spiritPlaysByPlayer || [];
  const winMapCharacters = useMemo(
    () => new Map((winnerByCharacter || []).map((w) => [Number(w.classId), Number(w.wins) || 0])),
    [winnerByCharacter]
  );
  const winMapSpirits = useMemo(
    () => new Map((winnerBySpirit || []).map((w) => [Number(w.spiritId), Number(w.wins) || 0])),
    [winnerBySpirit]
  );

  const sortedCharacters = useMemo(() => {
    return [...characters].sort((a, b) => {
      const gamesA = Number(a.games) || 0;
      const gamesB = Number(b.games) || 0;
      const winsA = winMapCharacters.get(Number(a.classId)) || 0;
      const winsB = winMapCharacters.get(Number(b.classId)) || 0;
      const wrA = gamesA > 0 ? winsA / gamesA : 0;
      const wrB = gamesB > 0 ? winsB / gamesB : 0;

      if (sortMetric === 'wins') return winsB - winsA || gamesB - gamesA;
      if (sortMetric === 'winrate') return wrB - wrA || gamesB - gamesA;
      return gamesB - gamesA || winsB - winsA;
    });
  }, [characters, winMapCharacters, sortMetric]);

  const sortedSpirits = useMemo(() => {
    return [...spiritUsage].sort((a, b) => {
      const gamesA = Number(a.picks) || 0;
      const gamesB = Number(b.picks) || 0;
      const winsA = winMapSpirits.get(Number(a.spiritId)) || 0;
      const winsB = winMapSpirits.get(Number(b.spiritId)) || 0;
      const wrA = gamesA > 0 ? winsA / gamesA : 0;
      const wrB = gamesB > 0 ? winsB / gamesB : 0;

      if (sortMetric === 'wins') return winsB - winsA || gamesB - gamesA;
      if (sortMetric === 'winrate') return wrB - wrA || gamesB - gamesA;
      return gamesB - gamesA || winsB - winsA;
    });
  }, [spiritUsage, winMapSpirits, sortMetric]);

  const baseChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          labels: {
            color: '#dfe7e2',
            font: { size: 12 }
          }
        },
        tooltip: {
          backgroundColor: 'rgba(5, 12, 10, 0.92)',
          titleColor: '#f7fbf9',
          bodyColor: '#f7fbf9',
          borderColor: 'rgba(255,255,255,0.08)',
          borderWidth: 1
        }
      },
      scales: {
        x: {
          ticks: { color: '#cdd9d3' },
          grid: { color: 'rgba(255,255,255,0.06)' }
        },
        y: {
          ticks: { color: '#cdd9d3' },
          grid: { color: 'rgba(255,255,255,0.06)' },
          beginAtZero: true
        }
      }
    }),
    []
  );

  const dailyChart = useMemo(() => {
    const source =
      trendInterval === 'weekly'
        ? gamesPerWeek
        : trendInterval === 'monthly'
        ? gamesPerMonth
        : trendInterval === 'yearly'
        ? gamesPerYear
        : gamesPerDay;

    const normalized = source
      .map((d) => {
        const dateField = d.startDate || d.date;
        const dateObj = dateField ? new Date(dateField) : null;
        return { ...d, dateObj };
      })
      .filter((d) => d.dateObj && !Number.isNaN(d.dateObj.getTime()))
      .sort((a, b) => a.dateObj - b.dateObj);

    const cutoff = trendRange ? new Date(Date.now() - (Number(trendRange) || 0) * 24 * 60 * 60 * 1000) : null;
    const filtered = cutoff ? normalized.filter((d) => d.dateObj >= cutoff) : normalized;

    const labels = filtered.map((d) => {
      if (trendInterval === 'weekly') return `Week of ${d.dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      if (trendInterval === 'monthly') return d.dateObj.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (trendInterval === 'yearly') return d.dateObj.getUTCFullYear().toString();
      return shortDate(d.dateObj);
    });

    const gamesData = filtered.map((d) => Number(d.games) || 0);
    const playerData = filtered.map((d) => Number(d.players) || 0);

    return {
      labels,
      datasets: [
        {
          label: 'Matches',
          data: gamesData,
          borderColor: '#f9b233',
          backgroundColor: 'rgba(249, 178, 51, 0.16)',
          tension: 0.32,
          fill: true,
          pointRadius: 3
        },
        {
          label: 'Players',
          data: playerData,
          borderColor: '#7bd5f5',
          backgroundColor: 'rgba(123, 213, 245, 0.14)',
          tension: 0.22,
          fill: false,
          pointRadius: 2
        }
      ]
    };
  }, [gamesPerDay, gamesPerWeek, gamesPerMonth, gamesPerYear, trendInterval, trendRange]);

  const spiritUsageChart = useMemo(() => {
    const top = spiritUsage.slice(0, 8);
    return {
      labels: top.map((s) => {
        const spirit = resolveReincarnation(Number(s.spiritId));
        return spirit ? spirit.name : `Spirit ${s.spiritId ?? '-'}`;
      }),
      datasets: [
        {
          label: 'Picks',
          data: top.map((s) => Number(s.picks) || 0),
          backgroundColor: top.map((_, idx) => palette[(idx + 6) % palette.length]),
          borderRadius: 10
        }
      ]
    };
  }, [spiritUsage]);

  const topCharactersChart = useMemo(() => {
    const winsData = sortedCharacters.map((c) => winMapCharacters.get(Number(c.classId)) || 0);
    const gamesData = sortedCharacters.map((c) => Number(c.games) || 0);

    return {
      labels: sortedCharacters.map((c) => {
        const character = resolveCharacter(Number(c.classId));
        return character ? character.name : `Character ${c.classId ?? '-'}`;
      }),
      datasets: [
        {
          label: 'Matches played',
          data: gamesData,
          backgroundColor: '#f9b233',
          borderRadius: 6
        },
        {
          label: 'Wins',
          data: winsData,
          backgroundColor: '#52e0ae',
          borderRadius: 6
        }
      ]
    };
  }, [sortedCharacters, winnerByCharacter]);

  const matchesPerSpiritChart = useMemo(() => {
    const gamesData = sortedSpirits.map((s) => Number(s.picks) || 0);
    const winsData = sortedSpirits.map((s) => winMapSpirits.get(Number(s.spiritId)) || 0);

    return {
      labels: sortedSpirits.map((s) => {
        const spirit = resolveReincarnation(Number(s.spiritId));
        return spirit ? spirit.name : `Spirit ${s.spiritId ?? '-'}`;
      }),
      datasets: [
        {
          label: 'Matches',
          data: gamesData,
          backgroundColor: '#f9b233',
          borderRadius: 6
        },
        {
          label: 'Wins',
          data: winsData,
          backgroundColor: '#52e0ae',
          borderRadius: 6
        }
      ]
    };
  }, [sortedSpirits, winnerBySpirit]);

  const winnerByCharacterChart = useMemo(() => {
    const top = winnerByCharacter.slice(0, 10);
    return {
      labels: top.map((c) => {
        const character = resolveCharacter(Number(c.classId));
        return character ? character.name : `Character ${c.classId ?? '-'}`;
      }),
      datasets: [
        {
          label: 'Wins',
          data: top.map((c) => Number(c.wins) || 0),
          backgroundColor: top.map((_, idx) => palette[(idx + 4) % palette.length]),
          borderRadius: 6
        }
      ]
    };
  }, [winnerByCharacter]);

  const winnerBySpiritChart = useMemo(() => {
    const top = winnerBySpirit.slice(0, 10);
    return {
      labels: top.map((s) => {
        const spirit = resolveReincarnation(Number(s.spiritId));
        return spirit ? spirit.name : `Spirit ${s.spiritId ?? '-'}`;
      }),
      datasets: [
        {
          label: 'Wins',
          data: top.map((s) => Number(s.wins) || 0),
          backgroundColor: top.map((_, idx) => palette[(idx + 5) % palette.length]),
          borderRadius: 6
        }
      ]
    };
  }, [winnerBySpirit]);

  const maxSpiritPicks = Math.max(...spiritUsage.map((s) => Number(s.picks) || 0), 1);

  return (
    <section className="statisticsPage separator">
      <header className="stats-header">
        <div>
          <p className="eyebrow">Deep dive</p>
          <h1>Game statistics</h1>
          <p className="subhead">A global view of matches played, rewards earned, and spirits collected.</p>
        </div>
        <div className="stats-highlight">
          <p>Total matches</p>
          <strong>{numberFormat(totals.games)}</strong>
          <span>{numberFormat(totals.uniquePlayers)} unique players</span>
        </div>
      </header>

      {loading && <div className="stat-alert">Loading data...</div>}
      {error && <div className="stat-alert error">{error}</div>}

      {!loading && (
        <>
          <div className="mode-switch">
            {modeStats.map((section) => (
              <button
                key={section.id}
                className={`switch-btn ${activeMode === section.id ? 'is-active' : ''}`}
                onClick={() => setActiveMode(section.id)}
              >
                {section.label}
              </button>
            ))}
          </div>

          <section className="stat-grid">
            <article className="stat-card">
              <p className="stat-label">Total rewards</p>
              <p className="stat-value">{numberFormat(totals.totalRewards)}</p>
              {Number(totals.spiritRewards) > 0 && (
                <p className="stat-hint">Including {numberFormat(totals.spiritRewards)} spirits</p>
              )}
            </article>
            <article className="stat-card">
              <p className="stat-label">Total time</p>
              <p className="stat-value">{formatSeconds(totals.totalDuration)}</p>
              <p className="stat-hint">Average duration {formatSeconds(totals.averageDuration)}</p>
            </article>
            <article className="stat-card">
              <p className="stat-label">{activeMode === 'Survivor' ? 'Monsters killed' : 'Kills'}</p>
              <p className="stat-value">
                {numberFormat(activeMode === 'Survivor' ? totals.totalMonsterKills : totals.totalKills)}
              </p>
              {activeMode !== 'Survivor' && (
                <p className="stat-hint">{numberFormat(totals.totalMonsterKills)} monsters</p>
              )}
            </article>
            <article className="stat-card">
              <p className="stat-label">Players per match</p>
              <p className="stat-value">{Number(totals.averagePlayers || 0).toFixed(1)}</p>
              <p className="stat-hint">{numberFormat(totals.totalPlayerSlots)} participations</p>
            </article>
          </section>

          <section className="charts-grid">
            <article className="chart-card wide">
              <header>
                <div>
                  <h3>Daily trend</h3>
                  <p className="muted small">Switch between daily, weekly, monthly, and yearly views</p>
                </div>
                <div className="trend-controls">
                  <div className="control-group">
                    <span>Interval</span>
                    <div className="pill-group">
                      {['daily', 'weekly', 'monthly', 'yearly'].map((key) => (
                        <button
                          key={key}
                          className={`pill-btn ${trendInterval === key ? 'is-active' : ''}`}
                          onClick={() => setTrendInterval(key)}
                        >
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="control-group">
                    <span>Range</span>
                    <div className="pill-group">
                      {[90, 180, 365].map((days) => (
                        <button
                          key={days}
                          className={`pill-btn ${Number(trendRange) === days ? 'is-active' : ''}`}
                          onClick={() => setTrendRange(days)}
                        >
                          {days}d
                        </button>
                      ))}
                      <button
                        className={`pill-btn ${trendRange === 0 ? 'is-active' : ''}`}
                        onClick={() => setTrendRange(0)}
                      >
                        All
                      </button>
                    </div>
                  </div>
                </div>
              </header>
              <div className="chart-shell giant">
                <Line
                  data={dailyChart}
                  options={{
                    ...baseChartOptions,
                    plugins: { ...baseChartOptions.plugins, legend: { display: true, labels: { color: '#dfe7e2' } } },
                    scales: {
                      ...baseChartOptions.scales,
                      x: { ...baseChartOptions.scales.x, ticks: { color: '#cdd9d3', maxRotation: 0, autoSkip: true } }
                    }
                  }}
                />
              </div>
            </article>
          </section>

          <div className="sort-switch">
            <span>Sort these charts by:</span>
            <div className="pill-group">
              <button
                className={`pill-btn ${sortMetric === 'matches' ? 'is-active' : ''}`}
                onClick={() => setSortMetric('matches')}
              >
                Matches
              </button>
              <button
                className={`pill-btn ${sortMetric === 'wins' ? 'is-active' : ''}`}
                onClick={() => setSortMetric('wins')}
              >
                Wins
              </button>
              <button
                className={`pill-btn ${sortMetric === 'winrate' ? 'is-active' : ''}`}
                onClick={() => setSortMetric('winrate')}
              >
                Win rate
              </button>
            </div>
          </div>

          <section className="charts-grid">
            <article className="chart-card wide">
              <header><h3>Matches per character</h3></header>
              <div className="chart-shell">
                <Bar
                  data={topCharactersChart}
                  options={{
                    ...baseChartOptions,
                    indexAxis: 'y'
                  }}
                />
              </div>
              <div className="mini-list">
                {sortedCharacters.map((c) => {
                  const character = resolveCharacter(Number(c.classId));
                  const wins = (winnerByCharacter || []).find((w) => Number(w.classId) === Number(c.classId))?.wins || 0;
                  const games = Number(c.games) || 0;
                  const wr = games > 0 ? Math.round((wins / games) * 1000) / 10 : 0;
                  return (
                    <div key={c.classId ?? 'unknown'} className="mini-row">
                      {character && <img src={character.icon} alt={character.name} />}
                      <div>
                        <p>{character ? character.name : `Character ${c.classId ?? '-'}`}</p>
                        <span className="muted tiny">{numberFormat(games)} matches - {numberFormat(wins)} wins - {wr}% WR</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="chart-card wide">
              <header><h3>Matches per spirit</h3></header>
              <div className="chart-shell">
                <Bar
                  data={matchesPerSpiritChart}
                  options={{
                    ...baseChartOptions,
                    indexAxis: 'y'
                  }}
                />
              </div>
              <div className="mini-list">
                {sortedSpirits.map((s) => {
                  const spirit = resolveReincarnation(Number(s.spiritId));
                  const wins = (winnerBySpirit || []).find((w) => Number(w.spiritId) === Number(s.spiritId))?.wins || 0;
                  const games = Number(s.picks) || 0;
                  const wr = games > 0 ? Math.round((wins / games) * 1000) / 10 : 0;
                  return (
                    <div key={s.spiritId ?? 'spirit-unknown'} className="mini-row">
                      {spirit ? <img src={spirit.icon} alt={spirit.name} /> : <div className="avatar-fallback">?</div>}
                      <div>
                        <p>{spirit ? spirit.name : `Spirit ${s.spiritId ?? '-'}`}</p>
                        <span className="muted tiny">{numberFormat(Number(s.picks) || 0)} picks - {numberFormat(wins)} wins - {wr}% WR</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>
          </section>

          <section className="charts-grid">
            <article className="chart-card">
              <header><h3>Winners by character</h3></header>
              <div className="chart-shell">
                <Bar
                  data={winnerByCharacterChart}
                  options={{
                    ...baseChartOptions,
                    indexAxis: 'y',
                    plugins: { ...baseChartOptions.plugins, legend: { display: false } }
                  }}
                />
              </div>
            </article>
            <article className="chart-card">
              <header><h3>Winners by spirit</h3></header>
              <div className="chart-shell">
                <Bar
                  data={winnerBySpiritChart}
                  options={{
                    ...baseChartOptions,
                    indexAxis: 'y',
                    plugins: { ...baseChartOptions.plugins, legend: { display: false } }
                  }}
                />
              </div>
            </article>
            <article className="chart-card">
              <header><h3>Top summoned spirits</h3></header>
              <div className="chart-shell">
                <Doughnut
                  data={spiritUsageChart}
                  options={{ plugins: { ...baseChartOptions.plugins, legend: { display: false } }, responsive: true, maintainAspectRatio: false }}
                />
              </div>
            </article>
          </section>
        </>
      )}
    </section>
  );
}

export default Statistics;












